import { addDays, addMonths, compareDates, daysInMonth, formatDate, isoWeekday, isValidDate, parseDate, } from './calendar.js';
import { MONTHS, NUMBER_WORDS, RELATIVE_DAYS, UNITS, WEEKDAYS } from './vocabulary.js';
// ─── Helpers ────────────────────────────────────────────────────────────────────────────────
/** Word boundaries that understand accented letters (JS `\b` does not). */
const B = '(?<![\\p{L}\\p{N}])';
const E = '(?![\\p{L}\\p{N}])';
/** Accent-tolerant pattern for a vocabulary word: «miércoles» also matches «miercoles». */
function word(value) {
    return value
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\s+/g, '\\s+')
        .replace(/[aàá]/g, '[aàá]')
        .replace(/[eèé]/g, '[eèé]')
        .replace(/[iíï]/g, '[iíï]')
        .replace(/[oòó]/g, '[oòó]')
        .replace(/[uúü]/g, '[uúü]')
        .replace(/[nñ]/g, '[nñ]')
        .replace(/[cç]/g, '[cç]')
        .replace(/'/g, "['’]");
}
function alternatives(values) {
    // Longest first, so «pasado mañana» wins over «mañana» at the same position.
    return [...new Set(values)]
        .sort((a, b) => b.length - a.length)
        .map(word)
        .join('|');
}
/** Lowercase, no accents, hyphen or underscore as space: how tags and dismissals are compared. */
export function normalize(value) {
    return value
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .replace(/[-_]+/g, ' ')
        .replace(/·/g, '')
        .replace(/\s+/g, ' ')
        .trim();
}
function lookup(table, value) {
    const key = normalize(value);
    for (const [name, result] of Object.entries(table)) {
        if (normalize(name) === key)
            return result;
    }
    return undefined;
}
function monthOf(value) {
    for (const [month, names] of Object.entries(MONTHS)) {
        if (names.some((name) => normalize(name) === normalize(value)))
            return Number(month);
    }
    return undefined;
}
function weekdayOf(value) {
    for (const [day, names] of Object.entries(WEEKDAYS)) {
        if (names.some((name) => normalize(name) === normalize(value)))
            return Number(day);
    }
    return undefined;
}
/** Next occurrence of an ISO weekday strictly after today: said on Friday, «el viernes» is next week. */
function nextWeekday(today, weekday) {
    const delta = (weekday - isoWeekday(today) + 7) % 7;
    return addDays(today, delta === 0 ? 7 : delta);
}
/** Day and month without year: this year if not past yet, otherwise next year. */
function nextDayMonth(today, day, month) {
    const { year } = parseDate(today);
    for (const candidateYear of [year, year + 1, year + 2, year + 3, year + 4]) {
        if (!isValidDate(candidateYear, month, day))
            continue;
        const candidate = formatDate({ year: candidateYear, month, day });
        if (compareDates(candidate, today) >= 0)
            return candidate;
    }
    return null;
}
/** «El N»: this month if today or later, otherwise the next month that has that day. */
function nextDayOfMonth(today, day) {
    if (day < 1 || day > 31)
        return null;
    const { year, month } = parseDate(today);
    for (let offset = 0; offset < 12; offset += 1) {
        const first = addMonths(formatDate({ year, month, day: 1 }), offset);
        const { year: y, month: m } = parseDate(first);
        if (day > daysInMonth(y, m))
            continue;
        const candidate = formatDate({ year: y, month: m, day });
        if (compareDates(candidate, today) >= 0)
            return candidate;
    }
    return null;
}
function expandYear(value) {
    const year = Number(value);
    return value.length === 2 ? 2000 + year : year;
}
/** Articles and determiners that can precede a weekday or «el N» and belong to the date. */
const WEEKDAY_LEAD = '(?:(?:el\\s+pr[óo]ximo|la\\s+pr[óo]xima|el\\s+proper|pr[óo]ximo|proper|este|aquest|el|del)\\s+)?';
const DAY_LEAD = "(?:(?:el|del|al)\\s+(?:d[ií]a\\s+)?|l['’])";
const NUMBER = `(\\d{1,3}|${alternatives(Object.keys(NUMBER_WORDS))})`;
const UNIT = `(${alternatives(Object.keys(UNITS))})`;
const MONTH = `(${alternatives(Object.values(MONTHS).flat())})`;
const WEEKDAY = `(${alternatives(Object.values(WEEKDAYS).flat())})`;
/** Numbers that are not dates: amounts, times, decimals, fractions. */
const NOT_AFTER_NUMBER = '(?![\\p{L}\\p{N}%€$:/]|[.,]\\d)';
const RULES = [
    // hoy, mañana, pasado mañana · avui, demà, demà passat. «la mañana» (the morning) is not a date.
    {
        pattern: new RegExp(`${B}(?<!(?:la|las|esta|cada|por\\s+la|de\\s+la)\\s+)(${alternatives(RELATIVE_DAYS.map(([w]) => w))})${E}`, 'giu'),
        resolve: (m, today) => {
            const offset = RELATIVE_DAYS.find(([w]) => normalize(w) === normalize(m[1] ?? ''))?.[1];
            return offset === undefined ? null : addDays(today, offset);
        },
    },
    // (el) (próximo) lunes · (el) (proper) dilluns — never «los lunes» / «cada lunes» (recurrences).
    {
        pattern: new RegExp(`${B}(?<!(?:los|els|cada)\\s+)${WEEKDAY_LEAD}${WEEKDAY}${E}`, 'giu'),
        resolve: (m, today) => {
            const weekday = weekdayOf(m[1] ?? '');
            return weekday === undefined ? null : nextWeekday(today, weekday);
        },
    },
    // en 3 días, dentro de dos semanas · d'aquí a 3 dies, en un mes
    {
        pattern: new RegExp(`${B}(?:en|dentro\\s+de|d['’]aqu[íi](?:\\s+a)?)\\s+${NUMBER}\\s+${UNIT}${E}`, 'giu'),
        resolve: (m, today) => {
            const raw = m[1] ?? '';
            const amount = /^\d+$/.test(raw) ? Number(raw) : lookup(NUMBER_WORDS, raw);
            const unit = lookup(UNITS, m[2] ?? '');
            if (!amount || !unit)
                return null;
            if (unit === 'month')
                return addMonths(today, amount);
            return addDays(today, unit === 'week' ? amount * 7 : amount);
        },
    },
    // la semana que viene, la próxima semana · la setmana que ve, la propera setmana → next Monday
    {
        pattern: new RegExp(`${B}(?:la\\s+)?(?:semana\\s+que\\s+viene|pr[óo]xima\\s+semana|setmana\\s+que\\s+ve|propera\\s+setmana)${E}`, 'giu'),
        resolve: (_m, today) => nextWeekday(today, 1),
    },
    // (a) fin de mes · (a) final de mes → last day of this month
    {
        pattern: new RegExp(`${B}(?:a\\s+)?(?:fin|final)\\s+de(?:l)?\\s+mes${E}`, 'giu'),
        resolve: (_m, today) => {
            const { year, month } = parseDate(today);
            return formatDate({ year, month, day: daysInMonth(year, month) });
        },
    },
    // 2026-09-15
    {
        pattern: new RegExp(`${B}(\\d{4})-(\\d{2})-(\\d{2})${E}`, 'gu'),
        resolve: (m) => {
            const [year, month, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
            return isValidDate(year, month, day) ? formatDate({ year, month, day }) : null;
        },
    },
    // (el) 15/9, 15/9/2027 · 15/09/27
    {
        pattern: new RegExp(`${B}(?:${DAY_LEAD})?(?<![\\d/])(\\d{1,2})/(\\d{1,2})(?:/(\\d{4}|\\d{2}))?(?![\\d/:])`, 'giu'),
        resolve: (m, today) => {
            const [day, month] = [Number(m[1]), Number(m[2])];
            if (m[3]) {
                const year = expandYear(m[3]);
                return isValidDate(year, month, day) ? formatDate({ year, month, day }) : null;
            }
            return nextDayMonth(today, day, month);
        },
    },
    // (el) 15 de septiembre (de 2027) · 15 de setembre, 2 d'octubre
    {
        pattern: new RegExp(`${B}(?:${DAY_LEAD})?(\\d{1,2})\\s+(?:de\\s+|d['’])${MONTH}(?:\\s+(?:de\\s+|del\\s+)?(\\d{4}))?${E}`, 'giu'),
        resolve: (m, today) => {
            const day = Number(m[1]);
            const month = monthOf(m[2] ?? '');
            if (!month)
                return null;
            if (m[3]) {
                const year = Number(m[3]);
                return isValidDate(year, month, day) ? formatDate({ year, month, day }) : null;
            }
            return nextDayMonth(today, day, month);
        },
    },
    // el 3, el día 3 · el dia 3 → next day 3 (never an amount, a time or a decimal)
    {
        pattern: new RegExp(`${B}${DAY_LEAD}(\\d{1,2})${NOT_AFTER_NUMBER}`, 'giu'),
        resolve: (m, today) => nextDayOfMonth(today, Number(m[1])),
    },
];
/**
 * Deadline words right before a date belong to it, so the title does not keep them dangling:
 * «Comprar pan para mañana» → «Comprar pan». They do not change the meaning: it is a due date.
 */
const DUE_BEFORE = new RegExp(`${B}(para|per\\s+a|per|antes\\s+de|antes|abans\\s+de|abans|hasta|fins\\s+a|fins)\\s+$`, 'iu');
/** Start markers right before a date: «desde el lunes», «a partir del 3», «des de dilluns». */
const START_BEFORE = new RegExp(`${B}(desde|des\\s+de|a\\s+partir\\s+de|a\\s+partir|des)\\s+$`, 'iu');
function findDates(text, today) {
    const found = [];
    for (const rule of RULES) {
        rule.pattern.lastIndex = 0;
        for (let match = rule.pattern.exec(text); match; match = rule.pattern.exec(text)) {
            const date = rule.resolve(match, today);
            if (date)
                found.push({ start: match.index, end: match.index + match[0].length, date });
        }
    }
    return found;
}
// ─── Tags ───────────────────────────────────────────────────────────────────────────────────
/** A tag: prefix, then a letter, then letters, digits, `_ - · '` (and `/` for hierarchical topics). */
const TAG = new RegExp(`(?<![\\p{L}\\p{N}_@#+./\\-])(?:([@+])([\\p{L}][\\p{L}\\p{N}_\\-·'’]*)|#([\\p{L}][\\p{L}\\p{N}_\\-·'’]*(?:/[\\p{L}\\p{N}][\\p{L}\\p{N}_\\-·'’]*)*))`, 'gu');
function findTags(text) {
    const tags = [];
    TAG.lastIndex = 0;
    for (let match = TAG.exec(text); match; match = TAG.exec(text)) {
        const prefix = (match[1] ?? '#');
        // Trailing joiners belong to the sentence, not to the tag («@casa-» at the end of a line).
        const raw = (match[2] ?? match[3] ?? '').replace(/[-_·'’/]+$/u, '');
        if (raw === '')
            continue;
        const start = match.index;
        const end = start + 1 + raw.length;
        tags.push({
            kind: 'tag',
            prefix,
            path: raw.split('/').filter((segment) => segment !== ''),
            start,
            end,
            text: text.slice(start, end),
        });
    }
    return tags;
}
// ─── Parse ──────────────────────────────────────────────────────────────────────────────────
function overlaps(a, b) {
    return a.start < b.end && b.start < a.end;
}
export function tokenKey(token) {
    return `${token.kind}:${normalize(token.text)}`;
}
/** Removes the accepted detections from the text and tidies the spaces left behind. */
function cleanTitle(text, tokens) {
    let title = '';
    let cursor = 0;
    for (const token of tokens) {
        title += `${text.slice(cursor, token.start)} `;
        cursor = token.end;
    }
    title += text.slice(cursor);
    const tidy = title
        .replace(/\s+/g, ' ')
        .replace(/\s+([,.;:!?)])/g, '$1')
        .replace(/([(])\s+/g, '$1')
        .trim()
        .replace(/[,;:]$/, '')
        .trim();
    // «Mañana llamar al fontanero» → «Llamar al fontanero»: keep the capital the sentence had.
    const leading = tokens[0]?.start === 0 && /^\p{Lu}/u.test(text);
    return leading ? tidy.charAt(0).toLocaleUpperCase('es') + tidy.slice(1) : tidy;
}
export function parseCapture(text, { today, dismissed = [] }) {
    parseDate(today); // fail early on a malformed «today»
    const skip = new Set(dismissed);
    // Tags first: «@lunes» is a tag, not a weekday.
    const tagCandidates = findTags(text).map((tag) => ({ ...tag, key: tokenKey(tag) }));
    // Dates that do not touch a tag; at the same place the longest wins.
    const dateCandidates = findDates(text, today)
        .filter((date) => !tagCandidates.some((tag) => overlaps(tag, date)))
        .sort((a, b) => a.start - b.start || b.end - b.start - (a.end - a.start));
    const dates = [];
    for (const candidate of dateCandidates) {
        if (dates.some((taken) => overlaps(taken, candidate)))
            continue;
        const before = START_BEFORE.exec(text.slice(0, candidate.start));
        const dateText = text.slice(candidate.start, candidate.end);
        // «a partir» and «des» alone only before a contracted article: «a partir del lunes».
        const bareMarker = before && /^(a\s+partir|des)$/iu.test(before[1] ?? '');
        const isStart = before !== null && (!bareMarker || /^(del|d['’])/iu.test(dateText));
        const deadline = isStart ? null : DUE_BEFORE.exec(text.slice(0, candidate.start));
        const start = isStart ? before.index : (deadline?.index ?? candidate.start);
        const token = {
            kind: isStart ? 'start' : 'due',
            date: candidate.date,
            start,
            end: candidate.end,
            text: text.slice(start, candidate.end),
        };
        dates.push({ ...token, key: tokenKey(token) });
    }
    // One due date and one start date: later ones stay in the title as written.
    const accepted = [];
    let due;
    let startOn;
    for (const token of dates) {
        if (skip.has(token.key))
            continue;
        if (token.kind === 'due' && !due) {
            due = token;
            accepted.push(token);
        }
        else if (token.kind === 'start' && !startOn) {
            startOn = token;
            accepted.push(token);
        }
    }
    const seenTags = new Set();
    const tags = [];
    for (const tag of tagCandidates) {
        if (skip.has(tag.key))
            continue;
        accepted.push(tag);
        // The same tag twice is removed from the title twice but assigned once.
        const identity = `${tag.prefix}${tag.path.map(normalize).join('/')}`;
        if (!seenTags.has(identity)) {
            seenTags.add(identity);
            tags.push(tag);
        }
    }
    accepted.sort((a, b) => a.start - b.start);
    const cleaned = cleanTitle(text, accepted);
    return {
        // A capture made only of detections («mañana @calle») keeps its text as the title.
        title: cleaned === '' ? text.trim().replace(/\s+/g, ' ') : cleaned,
        due_on: due?.date ?? null,
        start_on: startOn?.date ?? null,
        tags,
        tokens: accepted,
    };
}
//# sourceMappingURL=parser.js.map